import React, { CSSProperties, ReactElement, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { UIStickyContext, UIStickyEvent } from '~/Components/UISticky/UIStickyContext';

interface StickyProps {
  topOffset?: number;

  bottomOffset?: number;

  relative?: boolean;

  disableCompensation?: boolean;

  disableHardwareAcceleration?: boolean;

  children(state: StickyState): ReactElement;
}

interface StickyState {
  isSticky: boolean;

  wasSticky: boolean;

  distanceFromTop?: number;

  distanceFromBottom?: number;

  calculatedHeight?: number;

  style: CSSProperties;
}

export function UISticky({
  topOffset = 0,

  bottomOffset = 0,

  relative = false,

  disableCompensation = false,

  disableHardwareAcceleration = false,

  children,
}: StickyProps) {
  const context = useContext(UIStickyContext);

  if (!context) {
    throw new TypeError('Expected Sticky to be mounted within StickyContainer');
  }

  const { subscribe, unsubscribe, getParent } = context;


  const placeholderRef = useRef<HTMLDivElement | null>(null);

  const contentRef = useRef<HTMLElement | null>(null);

  const [state, setState] = useState<StickyState>({
    isSticky: false,

    wasSticky: false,

    style: {},
  });

  /**
   * Подписка на Container
   */
  useEffect(() => {
    subscribe(handleContainerEvent);

    return () => {
      unsubscribe(handleContainerEvent);
    };
  }, [subscribe, unsubscribe]);

  /**
   * Компенсация высоты placeholder
   *
   * useLayoutEffect нужен,
   * потому что это изменение DOM layout
   */
  useLayoutEffect(() => {
    const placeholder = placeholderRef.current;

    if (!placeholder) {
      return;
    }

    placeholder.style.paddingBottom = disableCompensation
      ? '0px'
      : `${state.isSticky ? state.calculatedHeight ?? 0 : 0}px`;
  }, [state.isSticky, state.calculatedHeight, disableCompensation]);

  function handleContainerEvent({
    distanceFromTop,

    distanceFromBottom,

    eventSource,
  }: UIStickyEvent) {
    const parent = getParent();

    if (!parent || !placeholderRef.current || !contentRef.current) {
      return;
    }

    let preventingStickyStateChanges = false;

    if (relative) {
      preventingStickyStateChanges = eventSource !== parent;

      distanceFromTop = -(parent.scrollTop + parent.offsetTop) + placeholderRef.current.offsetTop;
    }

    const placeholderClientRect = placeholderRef.current.getBoundingClientRect();

    const contentClientRect = contentRef.current.getBoundingClientRect();

    const calculatedHeight = contentClientRect.height;

    const bottomDifference = distanceFromBottom - bottomOffset - calculatedHeight;

    const wasSticky = state.isSticky;

    const isSticky = preventingStickyStateChanges
      ? wasSticky
      : distanceFromTop <= -topOffset && distanceFromBottom > -bottomOffset;

    const style: CSSProperties = !isSticky
      ? {}
      : {
          position: 'fixed',

          top:
            bottomDifference > 0
              ? relative
                ? parent.offsetTop - parent.offsetParent!.scrollTop
                : 0
              : bottomDifference,

          left: placeholderClientRect.left,

          width: placeholderClientRect.width,
        };

    if (!disableHardwareAcceleration) {
      style.transform = 'translateZ(0)';
    }

    setState({
      isSticky,

      wasSticky,

      distanceFromTop,

      distanceFromBottom,

      calculatedHeight,

      style,
    });
  }

  const element = children({
    isSticky: state.isSticky,

    wasSticky: state.wasSticky,

    distanceFromTop: state.distanceFromTop,

    distanceFromBottom: state.distanceFromBottom,

    calculatedHeight: state.calculatedHeight,

    style: state.style,
  });

  return (
    <div>
      <div ref={placeholderRef} />

      {/*
          В React 19 ref можно передавать
          как обычный prop
        */}

      {/*{children({ isSticky, style: stickyStyle })}*/}

      {React.cloneElement(element, {
        ref: (node: HTMLElement | null) => {
          contentRef.current = node;
        },
      })}
    </div>
  );
}
